import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  AlertTitle,
  Button,
  Container,
  Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

const PayPalCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const error = searchParams.get('error');
    const success = searchParams.get('success');
    const errorDescription = searchParams.get('error_description');
    const redirectPath = searchParams.get('redirect') || '/dashboard?tab=account';

    if (error) {
      setStatus('error');
      // Decode URL-encoded error description
      const decodedError = errorDescription
        ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
        : 'PayPal connection failed';
      setMessage(decodedError);

      // Redirect after showing error
      setTimeout(() => {
        navigate(redirectPath);
      }, 3000);
    } else if (success === 'true') {
      setStatus('success');
      setMessage('Your PayPal account has been connected and verified successfully.');

      // Redirect after showing success
      setTimeout(() => {
        navigate(redirectPath);
      }, 2000);
    } else {
      // Unexpected state - redirect to dashboard
      setStatus('error');
      setMessage('Unexpected callback state');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    }
  }, [searchParams, navigate]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            width: '100%',
          }}
        >
          {status === 'processing' && (
            <>
              <CircularProgress size={64} sx={{ mb: 3, color: '#0070ba' }} />
              <Typography variant="h5" gutterBottom>
                Connecting PayPal...
              </Typography>
              <Typography color="text.secondary">
                Please wait while we verify your account.
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
              </Box>
              <Typography variant="h5" gutterBottom color="success.main">
                PayPal Connected!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                {message}
              </Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>Verified</AlertTitle>
                Your PayPal account is now verified and ready for payouts.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Redirecting you back...
              </Typography>
            </>
          )}

          {status === 'error' && (
            <>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: 'error.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <ErrorIcon sx={{ fontSize: 48, color: 'error.main' }} />
              </Box>
              <Typography variant="h5" gutterBottom color="error.main">
                Connection Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                <AlertTitle>Error</AlertTitle>
                {message}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Redirecting you back to try again...
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AccountBalanceWalletIcon />}
                onClick={() => navigate('/dashboard?tab=account')}
              >
                Go to Settings
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default PayPalCallback;
